import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Download, Filter, Loader2, ScrollText, Search, User as UserIcon } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Pagination } from '@/components/pagination';
import { PageHeader, Segmented, CopyId } from '@/components/page';
import { EmptyState, Notice, ago, rowLink } from '@/components/workspace-ui';
import { CopyButton, UserAvatar, fmtDateTime } from '@/components/admin-ui';
import { useToast, useDebouncedValue } from '@/hooks';
import { cn } from '@/lib/utils';
import { auditApi } from '@/api';
import type { AuditCategory, AuditLogEntry, AuditQuery } from '@/api/audit';

const PAGE_SIZE = 12;

type Cat = 'all' | AuditCategory;
const CATS: { value: Cat; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'auth', label: 'Auth' },
  { value: 'oauth', label: 'OAuth' },
  { value: 'access', label: 'Access' },
  { value: 'admin', label: 'Admin' },
];

type Range = '24h' | '7d' | '30d' | 'all';
const RANGES: { value: Range; label: string; days: number }[] = [
  { value: '24h', label: 'Last 24 hours', days: 1 },
  { value: '7d', label: 'Last 7 days', days: 7 },
  { value: '30d', label: 'Last 30 days', days: 30 },
  { value: 'all', label: 'All time', days: 0 },
];

type Tone = 'bad' | 'warn' | 'neutral';
const toneOf = (action: string): Tone =>
  /failed|denied|revoked|locked|brute_force|suspicious/.test(action)
    ? 'bad'
    : /rotated|rate_limited|disabled|deleted|removed/.test(action)
      ? 'warn'
      : 'neutral';

const TONE_CLS: Record<Tone, string> = {
  neutral: 'bg-foreground/[0.06] text-foreground',
  warn: 'bg-warning/[0.14] text-warning',
  bad: 'bg-destructive/[0.12] text-destructive',
};

function ActionChip({ action, className }: { action: string; className?: string }) {
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-[5px] px-1.5 py-0.5 font-mono text-[12px] font-medium',
        TONE_CLS[toneOf(action)],
        className
      )}
    >
      {action}
    </span>
  );
}

const RES_LINK: Record<string, (id: string) => string> = {
  user: (id) => `/users/${id}`,
  organization: (id) => `/organizations/${id}`,
  product: (id) => `/products/${id}`,
  oauth_client: (id) => `/clients/${id}`,
};

const sinceFor = (range: Range) => {
  const days = RANGES.find((r) => r.value === range)?.days ?? 0;
  return days ? new Date(Date.now() - days * 864e5).toISOString() : undefined;
};

export function AuditPage() {
  const { toast } = useToast();
  const [params, setParams] = useSearchParams();
  const actor = params.get('user') ?? '';
  const eventId = params.get('event') ?? '';

  const [search, setSearch] = useState(params.get('q') ?? '');
  const [cat, setCat] = useState<Cat>('all');
  const [range, setRange] = useState<Range>(params.get('q') || eventId ? 'all' : '7d');
  const [since, setSince] = useState<string | undefined>(() =>
    params.get('q') || eventId ? undefined : sinceFor('7d')
  );
  const [page, setPage] = useState(1);
  const [exporting, setExporting] = useState(false);
  const debouncedSearch = useDebouncedValue(search);

  const filter: AuditQuery = {
    category: cat === 'all' ? undefined : cat,
    userId: actor && actor !== 'anon' ? actor : undefined,
    q: debouncedSearch.trim() || undefined,
    startDate: since,
  };

  const { data: counts } = useQuery({ queryKey: ['audit', 'summary'], queryFn: auditApi.summary });
  const { data: actors = [] } = useQuery({ queryKey: ['audit', 'actors'], queryFn: auditApi.actors });

  const { data, isLoading, isFetching, isError, error, refetch } = useQuery({
    queryKey: ['audit', 'list', filter, page],
    queryFn: () =>
      auditApi.listWithTotal({ ...filter, limit: PAGE_SIZE, offset: (page - 1) * PAGE_SIZE }),
    placeholderData: (prev) => prev,
  });
  const logs = data?.data ?? [];
  const total = data?.total ?? 0;

  // A deep-linked event (?event=<id>) opens its detail dialog.
  const { data: linked } = useQuery({
    queryKey: ['audit', 'event', eventId],
    queryFn: () => auditApi.get(eventId),
    enabled: !!eventId,
    retry: false,
  });
  const [opened, setOpened] = useState<AuditLogEntry | null>(null);
  const current = opened ?? (eventId ? (linked ?? null) : null);
  const closeEvent = () => {
    setOpened(null);
    if (eventId) {
      const next = new URLSearchParams(params);
      next.delete('event');
      setParams(next, { replace: true });
    }
  };

  const setActor = (id: string) => {
    const next = new URLSearchParams(params);
    if (id) next.set('user', id);
    else next.delete('user');
    setParams(next, { replace: true });
    setPage(1);
  };

  const filtersActive = !!(search || cat !== 'all' || actor || range !== 'all');
  const clearFilters = () => {
    setSearch('');
    setCat('all');
    setRange('all');
    setSince(undefined);
    setParams(new URLSearchParams(), { replace: true });
    setPage(1);
  };

  const pagination = useMemo(
    () => ({
      page,
      pageSize: PAGE_SIZE,
      total,
      totalPages: Math.max(1, Math.ceil(total / PAGE_SIZE)),
    }),
    [page, total]
  );

  const exportCsv = async () => {
    setExporting(true);
    try {
      const rows: AuditLogEntry[] = [];
      const LIMIT = 500;
      for (let offset = 0; offset < 10_000; offset += LIMIT) {
        const res = await auditApi.listWithTotal({ ...filter, limit: LIMIT, offset });
        rows.push(...res.data);
        if (!res.hasMore) break;
      }
      if (!rows.length) {
        toast({
          variant: 'destructive',
          title: 'Nothing to export',
          description: 'No events match the current filters.',
        });
        return;
      }
      const cell = (v: unknown) => {
        const s = String(v ?? '');
        return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
      };
      const head = [
        'id',
        'timestamp',
        'action',
        'resource',
        'resource_id',
        'user_id',
        'user_email',
        'ip_address',
        'user_agent',
        'metadata',
      ];
      const csv = [head.join(',')]
        .concat(
          rows.map((e) =>
            [
              e.id,
              e.timestamp,
              e.action,
              e.resource,
              e.resourceId,
              e.userId,
              e.actor?.email,
              e.ipAddress,
              e.userAgent,
              JSON.stringify(e.metadata ?? {}),
            ]
              .map(cell)
              .join(',')
          )
        )
        .join('\n');
      const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
      const a = Object.assign(document.createElement('a'), {
        href: url,
        download: `authrz-audit-${new Date().toISOString().slice(0, 10)}.csv`,
      });
      document.body.append(a);
      a.click();
      a.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast({
        title: 'Export ready',
        description: `${rows.length} event${rows.length === 1 ? '' : 's'} saved as CSV.`,
      });
    } catch (e) {
      toast({
        variant: 'destructive',
        title: 'Export failed',
        description: e instanceof Error ? e.message : 'Could not export the audit log.',
      });
    } finally {
      setExporting(false);
    }
  };

  const actorCell = (e: AuditLogEntry) => {
    if (e.userId && e.actor) {
      return (
        <Link to={`/users/${e.userId}`} className="flex min-w-0 items-center gap-2.5">
          <UserAvatar
            name={e.actor.name}
            email={e.actor.email}
            className="h-7 w-7 text-[10px]"
          />
          <span className="min-w-0">
            <b className="block max-w-[200px] truncate font-medium">
              {e.actor.name || e.actor.email}
            </b>
            <span className="block max-w-[200px] truncate font-mono text-xs text-muted-foreground">
              {e.actor.email}
            </span>
          </span>
        </Link>
      );
    }
    const metaEmail = typeof e.metadata?.email === 'string' ? e.metadata.email : null;
    return (
      <span className="flex min-w-0 items-center gap-2.5 text-muted-foreground">
        <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-foreground/[0.06]">
          <UserIcon className="h-3.5 w-3.5" />
        </span>
        <span className="min-w-0">
          <b className="block font-medium">{e.userId ? 'Deleted user' : 'Anonymous'}</b>
          <span className="block max-w-[200px] truncate font-mono text-xs">
            {e.userId ?? metaEmail ?? 'No session'}
          </span>
        </span>
      </span>
    );
  };

  return (
    <div>
      <PageHeader
        title="Audit log"
        description="Every sign-in, token and admin change across authrz.me. Events are read-only and newest first."
        actions={
          <Button variant="outline" onClick={exportCsv} disabled={exporting}>
            {exporting ? <Loader2 className="animate-spin" /> : <Download />}
            Export CSV
          </Button>
        }
      />

      <Card aria-label="Audit events" className="overflow-hidden">
        <div className="flex flex-col gap-2 border-b p-3 lg:flex-row lg:flex-wrap lg:items-center">
          <div className="relative w-full lg:w-72">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              type="search"
              aria-label="Search events"
              placeholder="Action, resource ID, IP or email"
              autoComplete="off"
              className="pl-9"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
            />
          </div>
          <div className="max-w-full overflow-x-auto">
            <Segmented<Cat>
              value={cat}
              onChange={(v) => {
                setCat(v);
                setPage(1);
              }}
              options={CATS.map((c) => ({ ...c, count: counts?.[c.value] }))}
            />
          </div>
          <div className="flex flex-col gap-2 sm:flex-row lg:ml-auto">
            <Select value={actor || 'all'} onValueChange={(v) => setActor(v === 'all' ? '' : v)}>
              <SelectTrigger aria-label="Actor" className="w-full sm:w-[190px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All actors</SelectItem>
                {actors.map((a) => (
                  <SelectItem key={a.id} value={a.id}>
                    {a.name || a.email}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={range}
              onValueChange={(v) => {
                setRange(v as Range);
                setSince(sinceFor(v as Range));
                setPage(1);
              }}
            >
              <SelectTrigger aria-label="Time range" className="w-full sm:w-[170px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {RANGES.map((r) => (
                  <SelectItem key={r.value} value={r.value}>
                    {r.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {isError ? (
          <div className="p-4">
            <Notice
              tone="bad"
              title="Couldn’t load the audit log"
              action={
                <Button size="sm" variant="outline" onClick={() => refetch()}>
                  Retry
                </Button>
              }
            >
              {error instanceof Error ? error.message : 'The request failed.'}
            </Notice>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Time</TableHead>
                <TableHead>Action</TableHead>
                <TableHead>Actor</TableHead>
                <TableHead>Resource</TableHead>
                <TableHead>IP address</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody className={cn(isFetching && !isLoading && 'opacity-70 transition-opacity')}>
              {isLoading ? (
                Array.from({ length: 8 }).map((_, i) => (
                  <TableRow key={i}>
                    <TableCell colSpan={5}>
                      <Skeleton className="h-8 w-full" />
                    </TableCell>
                  </TableRow>
                ))
              ) : logs.length === 0 ? (
                <TableRow className="hover:bg-transparent">
                  <TableCell colSpan={5} className="p-0">
                    <EmptyState
                      icon={ScrollText}
                      title="No events match"
                      body="Widen the time range or clear the filters to see more of the log."
                      action={
                        filtersActive ? (
                          <Button variant="outline" onClick={clearFilters}>
                            Clear filters
                          </Button>
                        ) : undefined
                      }
                    />
                  </TableCell>
                </TableRow>
              ) : (
                logs.map((e) => (
                  <TableRow key={e.id} className={rowLink} onClick={() => setOpened(e)}>
                    <TableCell className="whitespace-nowrap">
                      <span className="block font-mono text-xs tabular-nums">
                        {fmtDateTime(e.timestamp)}
                      </span>
                      <span className="block text-xs text-muted-foreground">{ago(e.timestamp)}</span>
                    </TableCell>
                    <TableCell>
                      <button
                        type="button"
                        className="rounded-[5px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        aria-label={`Open event ${e.action} at ${fmtDateTime(e.timestamp)}`}
                        onClick={(ev) => {
                          ev.stopPropagation();
                          setOpened(e);
                        }}
                      >
                        <ActionChip action={e.action} />
                      </button>
                    </TableCell>
                    <TableCell onClick={(ev) => ev.stopPropagation()}>{actorCell(e)}</TableCell>
                    <TableCell>
                      <span className="block text-[13px]">{e.resource}</span>
                      <span
                        className="block max-w-[220px] truncate font-mono text-xs text-muted-foreground"
                        title={e.resourceId ?? ''}
                      >
                        {e.resourceId ?? '—'}
                      </span>
                    </TableCell>
                    <TableCell className="font-mono text-xs tabular-nums text-muted-foreground">
                      {e.ipAddress ?? '—'}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        )}

        {total > 0 && (
          <Pagination
            className="px-4 pb-4"
            pagination={pagination}
            onPageChange={setPage}
            itemLabel="events"
          />
        )}
      </Card>

      <EventDialog
        entry={current}
        onClose={closeEvent}
        onFilterActor={(id) => {
          setActor(id);
          closeEvent();
        }}
      />
    </div>
  );
}

function EventDialog({
  entry: e,
  onClose,
  onFilterActor,
}: {
  entry: AuditLogEntry | null;
  onClose: () => void;
  onFilterActor: (id: string) => void;
}) {
  const metadata = JSON.stringify(e?.metadata ?? {}, null, 2);
  const link = e?.resourceId ? RES_LINK[e.resource]?.(e.resourceId) : undefined;
  const first = (e?.actor?.name || e?.actor?.email || '').split(/[\s@]/)[0];

  return (
    <Dialog open={!!e} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-xl">
        {e && (
          <>
            <DialogHeader>
              <DialogTitle className="leading-normal">
                <ActionChip action={e.action} className="text-[14px]" />
              </DialogTitle>
              <DialogDescription className="font-mono text-xs">
                {fmtDateTime(e.timestamp)} · {ago(e.timestamp)}
              </DialogDescription>
            </DialogHeader>

            <dl className="grid grid-cols-[104px_minmax(0,1fr)] items-center gap-x-4 gap-y-2.5 text-[13px]">
              <dt className="text-muted-foreground">Event ID</dt>
              <dd className="min-w-0">
                <CopyId value={e.id} />
              </dd>
              <dt className="text-muted-foreground">Timestamp</dt>
              <dd className="break-all font-mono text-xs">{e.timestamp}</dd>
              <dt className="text-muted-foreground">Actor</dt>
              <dd className="min-w-0">
                {e.userId && e.actor ? (
                  <>
                    <Link to={`/users/${e.userId}`} className="hover:underline">
                      {e.actor.name || e.actor.email}
                    </Link>
                    <span className="block font-mono text-xs text-muted-foreground">
                      {e.actor.email}
                    </span>
                  </>
                ) : e.userId ? (
                  <CopyId value={e.userId} />
                ) : (
                  <span className="text-muted-foreground">Anonymous</span>
                )}
              </dd>
              <dt className="text-muted-foreground">Resource</dt>
              <dd>
                {link ? (
                  <Link to={link} className="hover:underline">
                    {e.resource}
                  </Link>
                ) : (
                  e.resource
                )}
              </dd>
              {e.resourceId && (
                <>
                  <dt className="text-muted-foreground">Resource ID</dt>
                  <dd className="min-w-0">
                    <CopyId value={e.resourceId} />
                  </dd>
                </>
              )}
              {e.ipAddress && (
                <>
                  <dt className="text-muted-foreground">IP address</dt>
                  <dd>
                    <CopyId value={e.ipAddress} />
                  </dd>
                </>
              )}
              <dt className="text-muted-foreground">User agent</dt>
              <dd className="break-all font-mono text-xs">{e.userAgent || '—'}</dd>
            </dl>

            <div>
              <div className="mb-1.5 flex items-center justify-between">
                <span className="font-mono text-[11px] uppercase tracking-[0.06em] text-muted-foreground">
                  metadata
                </span>
                <CopyButton value={metadata} label="Copy" />
              </div>
              <pre className="max-h-64 overflow-auto rounded-md border border-border bg-foreground/[0.04] p-3 font-mono text-xs">
                {metadata}
              </pre>
            </div>

            <DialogFooter className="gap-2 sm:gap-0">
              {e.userId && e.actor && (
                <Button variant="outline" onClick={() => onFilterActor(e.userId!)}>
                  <Filter />
                  Events by {first}
                </Button>
              )}
              <Button variant="outline" onClick={onClose}>
                Close
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
